import { __assign, __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useRef, useState } from "react";
import { InputContainer, InputWrapper } from "./styles";
import { Controller } from "react-hook-form";
import { useTheme } from "styled-components";
import { Spinner } from "../Spinner";
import { IconExclamation, IconEye, IconEyeSlash } from "../icons";
import { NumberFormatBase } from "react-number-format";
import { currencyFormatter } from "../../utils/functions";
import { FieldInfo } from "../FieldInfo";
var maskValue = function (value, mask) {
    if (!value || value.length === 0)
        return "";
    var maskedValue = "";
    var valueIndex = 0;
    var letterRegex = /^[a-zA-ZÀ-ÿ~^´`]+$/;
    for (var i = 0; i < mask.length; i++) {
        var maskChar = mask[i];
        var inputChar = value[valueIndex];
        if (!inputChar)
            break;
        if (maskChar === "9") {
            if (/\d/.test(inputChar)) {
                maskedValue += inputChar;
                valueIndex++;
            }
            else {
                break;
            }
        }
        else if (maskChar === "a") {
            if (letterRegex.test(inputChar)) {
                maskedValue += inputChar;
                valueIndex++;
            }
            else {
                break;
            }
        }
        else if (maskChar === "*") {
            maskedValue += inputChar;
            valueIndex++;
        }
        else {
            maskedValue += maskChar;
            if (inputChar === maskChar) {
                valueIndex++;
            }
        }
    }
    return maskedValue;
};
export var Input = forwardRef(function (_a, ref) {
    var _b;
    var label = _a.label, info = _a.info, error = _a.error, isLoading = _a.isLoading, register = _a.register, mask = _a.mask, containerStyles = _a.containerStyles, isCurrency = _a.isCurrency, hasPrefix = _a.hasPrefix, minimumFractionDigits = _a.minimumFractionDigits, control = _a.control, iconLeft = _a.iconLeft, iconRight = _a.iconRight, props = __rest(_a, ["label", "info", "error", "isLoading", "register", "mask", "containerStyles", "isCurrency", "hasPrefix", "minimumFractionDigits", "control", "iconLeft", "iconRight"]);
    var theme = useTheme();
    var spinnerSize = parseInt(theme.components.input.input.fontSize[theme.device].substring(-2)) + 4;
    var _c = useState(false), passwordShowing = _c[0], setPasswordShowing = _c[1];
    var togglePasswordVisibility = function () {
        setPasswordShowing(function (prev) { return !prev; });
    };
    var isPasswordField = props.type === "password";
    var inputType = isPasswordField && passwordShowing ? "text" : props.type;
    var passwordIcon = isPasswordField ? (passwordShowing ? (_jsx(IconEyeSlash, { style: { cursor: "pointer" }, onClick: togglePasswordVisibility })) : (_jsx(IconEye, { style: { cursor: "pointer" }, onClick: togglePasswordVisibility }))) : (iconRight);
    var valueFormatted = props.value !== undefined
        ? mask
            ? maskValue(props.value.toString(), mask)
            : props.value
        : undefined;
    return (_jsxs(InputWrapper, { className: props.className, style: __assign({ width: (_b = props.style) === null || _b === void 0 ? void 0 : _b.width }, containerStyles), children: [label && _jsx("label", { children: label }), _jsxs(InputContainer, { "$hasIconLeft": !!iconLeft, "$hasIconRight": !!passwordIcon, "$hasError": !!error, children: [iconLeft && _jsx("div", { className: "icon-left", children: iconLeft }), isCurrency ? (_jsx(CurrencyInput, __assign({ control: control, hasPrefix: hasPrefix, minimumFractionDigits: minimumFractionDigits }, props, { type: inputType, ref: ref }))) : (_jsx(NormalInput, __assign({ isLoading: isLoading }, props, { value: valueFormatted, type: inputType, ref: ref, register: register, mask: mask, control: control, onChange: function (e) {
                            var _a;
                            if (mask) {
                                var maskedValue = maskValue(e.target.value, mask);
                                e.target.value = maskedValue;
                            }
                            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, e);
                        } }))), isLoading ? (_jsx(Spinner, { size: spinnerSize, color: theme.colors.neutral.light, style: {
                            position: "absolute",
                            right: 16,
                            top: "calc(50% - ".concat(spinnerSize / 2, "px)"),
                        } })) : error ? (_jsx(IconExclamation, { className: "error-icon" })) : (passwordIcon && _jsx("div", { className: "icon-right", children: passwordIcon }))] }), _jsx(FieldInfo, { isDisabled: props.disabled || isLoading, isError: !!error, children: error || info })] }));
});
var NormalInput = forwardRef(function (_a, ref) {
    var isLoading = _a.isLoading, register = _a.register, control = _a.control, mask = _a.mask, props = __rest(_a, ["isLoading", "register", "control", "mask"]);
    return mask && register ? (_jsx(Controller, { control: control, name: register.name, render: function (_a) {
            var _b = _a.field, value = _b.value, onChange = _b.onChange, field = __rest(_b, ["value", "onChange"]);
            return (_jsx("input", __assign({}, field, props, { disabled: isLoading || props.disabled, value: value, onChange: function (e) {
                    onChange(maskValue(e.target.value, mask));
                }, ref: function (e) {
                    if (register) {
                        register.ref(e);
                    }
                    if (ref) {
                        //@ts-ignore
                        ref.current = e;
                    }
                } })));
        } })) : (_jsx("input", __assign({}, props, { disabled: isLoading || props.disabled }, register, { ref: function (e) {
            if (register) {
                register.ref(e);
            }
            if (ref) {
                //@ts-ignore
                ref.current = e;
            }
        } })));
});
var CurrencyInput = forwardRef(function (_a, ref) {
    var control = _a.control, name = _a.name, hasPrefix = _a.hasPrefix, _b = _a.minimumFractionDigits, minimumFractionDigits = _b === void 0 ? 2 : _b, value = _a.value, register = _a.register, isLoading = _a.isLoading, props = __rest(_a, ["control", "name", "hasPrefix", "minimumFractionDigits", "value", "register", "isLoading"]);
    var multiplicator = Math.pow(10, minimumFractionDigits);
    var inputElementRef = useRef(null);
    var assignInputRef = function (el) {
        inputElementRef.current = el;
        if (typeof ref === "function") {
            ref(el);
        }
        else if (ref) {
            ref.current = el;
        }
    };
    var handleKeyDownCommon = function (e) {
        if (e.key === "0" && e.currentTarget.value.length === 0) {
            setTimeout(function () {
                var el = inputElementRef.current;
                if (el) {
                    var end = el.value.length;
                    try {
                        el.setSelectionRange(end, end);
                    }
                    catch (_a) { }
                }
            }, 1);
        }
        // props.onKeyDown?.(e);
    };
    return control ? (_jsx(Controller, { name: name || "", control: control, render: function (_a) {
            var field = _a.field;
            return (_jsx(NumberFormatBase, __assign({ format: function (e) {
                    return currencyFormatter(parseFloat(e), hasPrefix, minimumFractionDigits, Number(props.max), Number(props.min));
                }, value: Number(value) * multiplicator }, props, { onKeyDown: function (e) {
                    var _a;
                    handleKeyDownCommon(e);
                    if (e.key === "Backspace") {
                        var el = inputElementRef.current;
                        var digits = ((el === null || el === void 0 ? void 0 : el.value) || "").replace(/\D/g, "");
                        var numeric = digits.length ? parseInt(digits, 10) : 0;
                        if (numeric === 0) {
                            e.preventDefault();
                            (_a = field.onChange) === null || _a === void 0 ? void 0 : _a.call(field, undefined);
                            if (el) {
                                el.value = "";
                                try {
                                    el.setSelectionRange(0, 0);
                                }
                                catch (_b) { }
                            }
                        }
                    }
                }, onValueChange: function (e) {
                    var _a;
                    if (e.floatValue === 0) {
                        setTimeout(function () {
                            var el = inputElementRef.current;
                            if (el) {
                                el.setSelectionRange(el.value.length, el.value.length);
                            }
                        }, 1);
                    }
                    (_a = field.onChange) === null || _a === void 0 ? void 0 : _a.call(field, e.floatValue ? e.floatValue / multiplicator : undefined);
                }, defaultValue: props.defaultValue, disabled: isLoading || props.disabled, type: "text", getInputRef: assignInputRef })));
        } })) : (_jsx(NumberFormatBase, __assign({ format: function (e) {
            return currencyFormatter(parseFloat(e), hasPrefix, minimumFractionDigits, Number(props.max), Number(props.min));
        } }, props, { value: Number(value) * multiplicator, defaultValue: props.defaultValue, disabled: isLoading || props.disabled, onKeyDown: function (e) {
            var _a;
            handleKeyDownCommon(e);
            if (e.key === "Backspace") {
                var el = inputElementRef.current;
                var digits = ((el === null || el === void 0 ? void 0 : el.value) || "").replace(/\D/g, "");
                var numeric = digits.length ? parseInt(digits, 10) : 0;
                if (numeric === 0) {
                    e.preventDefault();
                    (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, {
                        target: {
                            //@ts-ignore
                            value: undefined,
                        },
                    });
                    if (el) {
                        el.value = "";
                        try {
                            el.setSelectionRange(0, 0);
                        }
                        catch (_b) { }
                    }
                }
            }
        }, onChange: undefined, onValueChange: function (e) {
            var _a;
            if (e.floatValue === 0) {
                setTimeout(function () {
                    var el = inputElementRef.current;
                    if (el) {
                        el.setSelectionRange(el.value.length, el.value.length);
                    }
                }, 1);
            }
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, {
                target: {
                    //@ts-ignore
                    value: e.floatValue || e.floatValue === 0
                        ? e.floatValue / multiplicator
                        : undefined,
                },
            });
        }, type: "text", getInputRef: assignInputRef })));
});
//# sourceMappingURL=index.js.map